import React, { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { muted, primary, card, store } from './mbApi';
import { WorkflowVisual } from './WorkflowVisual';
import { RoleGuide } from './RoleGuide';
import { DAILY, RESULTS, FAQ, ADMIN } from './guideContent';

const LINK_RE = /(https?:\/\/[^\s)<>]+|(?:developers|business)\.facebook\.com(?:\/[\w\-/]*)?|@(?:BotFather|JsonDumpBot|userinfobot))/g;
const hrefOf = (m) => (m.startsWith('@') ? `https://t.me/${m.slice(1)}` : m.startsWith('http') ? m : `https://${m}`);

const Linkify = ({ text }) => text.split(LINK_RE).map((part, i) => (i % 2 ? (
  <a key={`${part}-${i}`} href={hrefOf(part)} target="_blank" rel="noopener noreferrer"
    className="text-[#F77F00] underline underline-offset-2 hover:text-[#ff9a33] break-all" data-testid="mb-guide-link">{part}</a>
) : part));

const List = ({ items }) => (
  <ul className={`space-y-1.5 text-xs ${muted} leading-relaxed`}>{items.map((it) => <li key={it}><Linkify text={it} /></li>)}</ul>
);

const Item = ({ id, title, children }) => (
  <AccordionItem value={id} className="border-[hsl(var(--border))]">
    <AccordionTrigger className={`text-sm ${primary} py-2.5`} data-testid={`mb-guide-${id}`}>{title}</AccordionTrigger>
    <AccordionContent>{children}</AccordionContent>
  </AccordionItem>
);

const Group = ({ label, testid, children }) => (
  <div className="mt-3" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#F77F00] mb-0.5">{label}</p>
    {children}
  </div>
);

const sections = (list) => list.map((s) => <Item key={s.id} id={s.id} title={s.title}><List items={s.items} /></Item>);

export const GuideCard = ({ canManage = false, embedded = false }) => {
  const [open, setOpen] = useState(() => embedded || store.get('mb-guide-open') !== '0');
  const toggle = () => { store.set('mb-guide-open', open ? '0' : '1'); setOpen(!open); };
  return (
    <div className={embedded ? 'mt-2' : card} data-testid="mb-guide-card">
      {!embedded && (
        <button onClick={toggle} className="w-full flex items-center justify-between min-h-10" data-testid="mb-guide-toggle">
          <span className={`text-sm font-semibold ${primary} flex items-center gap-2`}><BookOpen size={15} className="text-[#F77F00]" /> Cara Guna Marketing Hub</span>
          <span className={`text-xs ${muted} flex items-center gap-1`}>{open ? 'Tutup' : 'Buka panduan'} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
        </button>
      )}
      {open && (
        <Accordion type="single" collapsible defaultValue="roles" data-testid="mb-guide-sections">
          <Group label="Kerja Harian" testid="mb-guide-group-daily">
            <Item id="roles" title="Panduan ikut peranan (Kaer / Firzan / Azrai)"><RoleGuide /></Item>
            <Item id="visual" title="Carta aliran kerja (visual)"><WorkflowVisual /></Item>
            {sections(DAILY)}
          </Group>
          <Group label="Hasil & Pembelajaran" testid="mb-guide-group-results">{sections(RESULTS)}</Group>
          <Group label="Masalah Biasa" testid="mb-guide-group-faq">{sections(FAQ)}</Group>
          <Group label="Setup Admin" testid="mb-guide-group-admin">
            {!canManage && (
              <p className={`text-[11px] ${muted} pt-1`} data-testid="mb-guide-admin-note">
                Panduan setup dibuka untuk semua supaya team faham. Tab Tetapan (Simpan, Check, Sync) hanya nampak pada manager; hidup/matikan modul hanya admin.
              </p>
            )}
            {sections(ADMIN)}
          </Group>
        </Accordion>
      )}
    </div>
  );
};
